import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check, Clock, Bell, Loader2, ArrowRight } from 'lucide-react';
import ZenitThinking from '../../../components/ui/ZenitThinking';
import WizardIgService from '../../../services/wizardIg.service';
import { WizButton } from '../wizardUi';

const STAGE_ORDER = ['agent', 'avatar', 'workflow'];

function StageRow({ status, label }) {
  return (
    <div
      className={`flex items-center gap-3 rounded-2xl border bg-white/[0.04] px-4 py-3 text-[14px] font-medium transition-colors ${
        status === 'active' ? 'border-primary/50' : 'border-white/[0.08]'
      } ${status === 'pending' ? 'text-white/45' : 'text-foreground'}`}
    >
      <span className="flex h-5 w-5 shrink-0 items-center justify-center">
        {status === 'done' && <Check size={17} strokeWidth={3} className="text-emerald-400" />}
        {status === 'active' && <Loader2 size={16} className="animate-spin text-primary" />}
        {status === 'pending' && <Clock size={16} />}
      </span>
      {label}
    </div>
  );
}

export default function Step7Build({ wizard }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const started = useRef(false);

  useEffect(() => {
    if (started.current || wizard.build.status !== 'idle') return;
    started.current = true;
    wizard.setBuildStatus('running');
    WizardIgService.startBuild(wizard, {
      onStageChange: (stage, status) => wizard.setBuildStage(stage, status),
    }).then(() => {
      wizard.setBuildStatus('done');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const done = wizard.build.status === 'done';

  return (
    <div className="mx-auto flex h-full w-full max-w-md flex-col items-center justify-center gap-4 py-2 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/20 p-3.5 animate-glow-pulse">
        {done ? <Check size={26} className="text-emerald-400" /> : <ZenitThinking size={28} />}
      </div>
      <h3 className="text-[24px] font-medium leading-tight tracking-tight lg:text-[30px]">{t('wizardIg.title5')}</h3>
      <p className="max-w-[40ch] text-[14px] leading-relaxed text-white/55 lg:text-[15px]">{t('wizardIg.sub5')}</p>

      <div className="mt-1 flex w-full flex-col gap-2.5">
        {STAGE_ORDER.map((stage) => (
          <StageRow
            key={stage}
            status={wizard.build.stages[stage]}
            label={t(`wizardIg.build${stage[0].toUpperCase()}${stage.slice(1)}`)}
          />
        ))}
      </div>

      <div className="relative mt-1 h-2 w-full overflow-hidden rounded-full bg-white/10">
        {done ? (
          <div className="h-full w-full rounded-full bg-primary" />
        ) : (
          <div className="absolute inset-y-0 w-2/5 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#c084fc] animate-build-slide" />
        )}
      </div>

      {done ? (
        <WizButton className="mt-2 w-full" onClick={() => navigate('/home')}>
          {t('wizardIg.buildDoneCta')}
          <ArrowRight size={17} />
        </WizButton>
      ) : (
        <div className="mt-1 flex items-center gap-2 text-[12px] text-white/45">
          <Bell size={14} />
          {t('wizardIg.leaveNote')}
        </div>
      )}
    </div>
  );
}
